import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { ISkippedReplayResponse, ISkippedReplayTable } from '../sync-monitoring.models';
import { SyncMonitoringService } from '../sync-monitoring.service';

/** The table to replay and the audit run the operator saw: a table audited again since is not replayed (notLatestRun). */
export interface IReplaySkippedDialogData {
	environmentId: string;
	environmentName: string;
	table: { tableName: string; auditRunId: string };
}

/** Shows the dry run of a replay of skipped items, then queues it for the tables that are not blocked, and closes with the API's answer. */
@Component({
	selector: 'app-replay-skipped-dialog',
	templateUrl: './replay-skipped-dialog.component.html',
	styleUrls: ['../audit-dialogs.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ReplaySkippedDialogComponent implements OnInit {
	public isLoading = true;
	public ready: ISkippedReplayTable[] = [];
	public blocked: ISkippedReplayTable[] = [];
	/** The replay's answer once sent; closing the dialog hands it to the page. */
	private result: ISkippedReplayResponse | undefined;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IReplaySkippedDialogData,
		public dialogRef: MatDialogRef<ReplaySkippedDialogComponent, ISkippedReplayResponse>,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.syncMonitoringService.replaySkipped(this.data.environmentId, [this.data.table], true).subscribe({
			next: (response) => {
				this.ready = response.tables.filter((table) => !table.blockedReason);
				this.blocked = response.tables.filter((table) => !!table.blockedReason);
				this.isLoading = false;
			},
			error: () => {
				this.isLoading = false;
				this.notificationService.notify(['administration.sync-monitoring.replay-dialog.errors.dry-run-failed'], [], 'error');
			},
		});
	}

	/** The most items the replay sends, summed over the tables that are not blocked. */
	get willSendAtMost(): number {
		return this.ready.reduce((sum, table) => sum + (table.willSendAtMost ?? 0), 0);
	}

	/** Queues exactly the tables and runs the dry run showed, so a table audited again since is reported as notLatestRun. */
	onReplay(): void {
		if (this.isLoading || this.ready.length === 0) {
			return;
		}
		this.isLoading = true;
		this.dialogRef.disableClose = true;
		const tables = this.ready.map((table) => ({ tableName: table.tableName, auditRunId: table.auditRunId }));
		this.syncMonitoringService.replaySkipped(this.data.environmentId, tables, false).subscribe({
			next: (response) => {
				const notQueued = response.tables.filter((table) => !table.queued);
				if (notQueued.length === 0) {
					this.dialogRef.close(response);
					return;
				}
				// Some tables were blocked after the dry run: show why, and report what was queued on close.
				this.result = response;
				this.ready = [];
				this.blocked = notQueued;
				this.isLoading = false;
				this.dialogRef.disableClose = false;
			},
			error: () => {
				this.isLoading = false;
				this.dialogRef.disableClose = false;
				this.notificationService.notify(['administration.sync-monitoring.replay-dialog.errors.replay-failed'], [], 'error');
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close(this.result);
	}
}
